import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, ArrowLeft, Loader2, Phone, MessageCircle, CalendarPlus, Tag } from "lucide-react";
import { BRAND } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setStructuredData(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setNotFound(err?.response?.status === 404);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!data?.post) return;
    const p = data.post;
    const pageUrl = `${window.location.origin}/blog/${p.slug}`;
    const img = resolveImg(p.featured_image_url);
    document.title = (p.meta_title || p.title) + " — BarknFurs Grooming Studio";
    setMeta("description", p.meta_description || p.excerpt);
    setMeta("og:title", p.meta_title || p.title, "property");
    setMeta("og:description", p.meta_description || p.excerpt, "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", pageUrl, "property");
    if (img) setMeta("og:image", img, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", p.meta_title || p.title);
    setMeta("twitter:description", p.meta_description || p.excerpt);
    if (img) setMeta("twitter:image", img);
    setCanonical(pageUrl);
    setStructuredData("blog-jsonld", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: p.title,
      description: p.meta_description || p.excerpt,
      image: img ? [img] : undefined,
      datePublished: p.published_at,
      dateModified: p.updated_at,
      author: { "@type": "Organization", name: p.author_name || "BarknFurs Grooming Studio" },
      publisher: {
        "@type": "Organization",
        name: "BarknFurs Grooming Studio",
        logo: { "@type": "ImageObject", url: BRAND.logoUrl },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
      keywords: (p.tags || []).join(", ") || undefined,
    });
  }, [data]);

  // GA4 engagement tracking: scroll depth + read time per post
  useEffect(() => {
    if (!data?.post) return;
    const slugVal = data.post.slug;
    const titleVal = data.post.title;
    const cat = data.post.category || "General";
    const fired = { scroll: new Set(), time: new Set(), complete: false };
    const startTs = Date.now();

    const sendEvent = (name, params) => {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", name, {
          event_category: "blog_engagement",
          blog_slug: slugVal,
          blog_title: titleVal,
          blog_category: cat,
          ...params,
        });
      }
    };

    const checkScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((scrolled / total) * 100));
      for (const milestone of [25, 50, 75, 90]) {
        if (pct >= milestone && !fired.scroll.has(milestone)) {
          fired.scroll.add(milestone);
          sendEvent("blog_scroll", { percent: milestone });
        }
      }
      // read-complete: 90%+ scroll AND > 60s on page
      if (!fired.complete && pct >= 90 && Date.now() - startTs > 60_000) {
        fired.complete = true;
        sendEvent("blog_read_complete", {
          read_time_seconds: Math.round((Date.now() - startTs) / 1000),
        });
      }
    };

    const timers = [];
    for (const sec of [30, 60, 180, 300]) {
      timers.push(setTimeout(() => {
        if (!fired.time.has(sec)) {
          fired.time.add(sec);
          sendEvent("blog_read_time", { seconds: sec });
        }
      }, sec * 1000));
    }

    window.addEventListener("scroll", checkScroll, { passive: true });
    // Run once after layout settles
    const initId = setTimeout(checkScroll, 500);

    return () => {
      window.removeEventListener("scroll", checkScroll);
      clearTimeout(initId);
      timers.forEach(clearTimeout);
    };
  }, [data]);

  // GA4 conversion: CTA clicks inside a blog post
  const trackBlogCta = (cta) => {
    if (!data?.post) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "generate_lead", {
      event_category: "conversion",
      event_label: `blog_cta_${cta}`,
      method: cta,
      blog_slug: data.post.slug,
      blog_title: data.post.title,
    });
  };


  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-7 h-7 text-[#FC5106] animate-spin" /></div>;
  }
  if (notFound || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-3xl font-bold text-white">Post not found</h1>
        <p className="text-white/60 mt-2">The article you're looking for may have been moved or unpublished.</p>
        <button onClick={() => navigate("/blog")} className="btn-primary mt-6 !py-2.5 !px-5 !text-sm">Back to Blog</button>
      </div>
    );
  }

  const { post, related } = data;
  const img = resolveImg(post.featured_image_url);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6" data-testid="blog-back-link">
        <ArrowLeft className="w-4 h-4" /> All posts
      </Link>

      <header className="space-y-4 mb-8">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="uppercase tracking-wider text-[#C9BCFF]">{post.category || "General"}</span>
          <span className="text-white/30">·</span>
          <span className="inline-flex items-center gap-1 text-white/50"><CalendarDays className="w-3 h-3" />{formatDate(post.published_at)}</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
          {post.title}
        </h1>
        {post.excerpt && <p className="text-white/70 text-lg">{post.excerpt}</p>}
      </header>

      {img && (
        <figure className="rounded-2xl overflow-hidden border border-white/10 mb-8 bg-[#1F0A66]/40">
          <img
            src={img}
            alt={post.featured_image_alt || post.title}
            loading="eager"
            className="w-full h-auto object-cover"
          />
          {post.featured_image_alt && (
            <figcaption className="text-xs text-white/40 px-4 py-2">{post.featured_image_alt}</figcaption>
          )}
        </figure>
      )}

      <div
        className="blog-prose"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
        data-testid="blog-content"
      />

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-white/10">
          <Tag className="w-4 h-4 text-white/40" />
          {post.tags.map((t) => (
            <span key={t} className="text-xs text-white/70 bg-white/5 border border-white/10 rounded-full px-3 py-1">#{t}</span>
          ))}
        </div>
      )}

      {/* Strong CTA block */}
      <section className="mt-12 rounded-3xl bg-gradient-to-br from-[#FC5106]/20 via-transparent to-white/10 border border-white/10 p-6 sm:p-10">
        <h2 className="font-heading text-2xl font-bold text-white">Book a grooming session</h2>
        <p className="text-white/60 mt-2">
          Studio or doorstep grooming in Bhubaneswar · Certified groomers · Pet-safe products
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          <Link to="/contact" onClick={() => trackBlogCta("book")} data-testid="post-cta-book" className="inline-flex items-center gap-2 btn-primary !py-2.5 !px-5 !text-sm">
            <CalendarPlus className="w-4 h-4" /> Book Pet Grooming Now
          </Link>
          <a href={`tel:${BRAND.phoneRaw}`} onClick={() => trackBlogCta("call")} data-testid="post-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-[#C9BCFF] hover:bg-white/20 rounded-full px-5 py-2.5 text-sm font-semibold">
            <Phone className="w-4 h-4" /> Call Now
          </a>
          <a href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi BarknFurs — I read your blog about " + post.title)}`} target="_blank" rel="noreferrer"
            onClick={() => trackBlogCta("whatsapp")}
            data-testid="post-cta-whatsapp"
            className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded-full px-5 py-2.5 text-sm font-semibold">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      </section>

      {related && related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-2xl font-bold text-white mb-5">Related reads</h2>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="related-posts">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/blog/${r.slug}`}
                className="group block rounded-2xl overflow-hidden bg-[#2E0F94] border border-white/10 hover:border-[#FC5106]/40"
              >
                {r.featured_image_url && (
                  <div className="aspect-[16/10] overflow-hidden bg-[#1F0A66]/40">
                    <img
                      src={resolveImg(r.featured_image_url)}
                      alt={r.featured_image_alt || r.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-[#C9BCFF]">{r.category || "General"}</p>
                  <h3 className="text-white text-sm font-semibold mt-1 group-hover:text-[#FC5106] line-clamp-2">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
